/**
 * Mobile PWA — deck API client (Space-level v2).
 *
 * 入口：扫桌面 QR → pair handoff (无 JWT) → mint 24h mobile JWT → 存 localStorage.
 * 之后所有 API 走 mobile JWT.
 */

import { TOKEN_KEY } from './auth'

export const DECK_BASE = 'https://lab.hiq.earth/deck'

export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: string) {
    super(message)
    this.name = 'ApiError'
  }
}

function authHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${DECK_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...init.headers,
    },
  })
  if (!res.ok) {
    let body = ''
    try { body = await res.text() } catch {}
    throw new ApiError(`HTTP ${res.status}`, res.status, body)
  }
  return (await res.json()) as T
}

// ── Uploads（拍照 / 选图 / 选文件 / 录音 → 预签名直传对象存储）──────────

/** PUT 一个 Blob 到预签名 URL，带上传进度回调（fetch 拿不到上传进度，用 XHR）。 */
function putWithProgress(
  url: string,
  body: Blob,
  mime: string,
  onProgress?: (frac: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', mime)
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total)
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new ApiError(`upload PUT HTTP ${xhr.status}`, xhr.status))
    }
    xhr.onerror = () => reject(new ApiError('upload PUT failed', 0))
    xhr.send(body)
  })
}

/** 录音 mime → object key 后缀（纯展示用，deck 转写读 meta.mime 不读后缀）。 */
function audioExt(mime: string): string {
  const m = mime.toLowerCase()
  if (m.includes('mp4') || m.includes('m4a') || m.includes('aac')) return '.m4a'
  if (m.includes('ogg') || m.includes('opus')) return '.ogg'
  if (m.includes('mpeg') || m.includes('mp3')) return '.mp3'
  if (m.includes('wav')) return '.wav'
  return '.webm'
}

/** 附件引用 — 跟着 task / message 一起发给桌面。 */
export interface AttachmentRef {
  upload_id: string
  kind: 'image' | 'file'
}

/** 选好的文件 → /uploads/init 拿预签名 PUT → 直传对象存储 → 返回 upload_id。
 *  字节直传，不经 deck。25MB 上限由 deck 校验，超限抛 ApiError(413/422)。*/
export async function uploadFile(file: File): Promise<AttachmentRef> {
  const kind: 'image' | 'file' = file.type.startsWith('image/') ? 'image' : 'file'
  const mime = file.type || 'application/octet-stream'
  const init = await apiFetch<{ upload_id: string; put_url: string }>('/dispatch/uploads/init', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name || 'file', mime, size: file.size, kind }),
  })
  const putRes = await fetch(init.put_url, { method: 'PUT', headers: { 'Content-Type': mime }, body: file })
  if (!putRes.ok) throw new ApiError(`upload PUT HTTP ${putRes.status}`, putRes.status)
  return { upload_id: init.upload_id, kind }
}

/** 录音 Blob → /uploads/init 拿预签名 PUT → 直传对象存储 → 返回 upload_id。
 *  字节直传不经 deck；音频上限 64MB 由 deck 校验。onProgress 给上传进度条。*/
export async function uploadAudio(audio: Blob, onProgress?: (frac: number) => void): Promise<string> {
  const mime = audio.type || 'audio/webm'
  const init = await apiFetch<{ upload_id: string; put_url: string }>('/dispatch/uploads/init', {
    method: 'POST',
    body: JSON.stringify({ filename: `recording${audioExt(mime)}`, mime, size: audio.size, kind: 'audio' }),
  })
  await putWithProgress(init.put_url, audio, mime, onProgress)
  return init.upload_id
}

export interface UploadInfo {
  upload_id: string
  filename: string
  mime: string
  size: number
  kind: string
  get_url: string
}

/** 拉一个 upload 的预签名 GET（手机下载桌面产物 / 预览自己传的图）。 */
export async function getUpload(uploadId: string): Promise<UploadInfo> {
  return await apiFetch(`/dispatch/uploads/${uploadId}`)
}

// ── Types ────────────────────────────────────────────────────────

export interface SpaceOut {
  id: string
  worker_id: string
  space_id: string
  title: string | null
  paired_at: string
  last_used_at: string
}

export interface SessionOut {
  /** v2: 路由元数据 + 临时 title (从 stream:session:{sid} 首条 task_new prompt 派生).
   * Stream 失效后 title=null, UI 用 "会话 {id_slice}" fallback. */
  id: string
  worker_id: string
  space_id: string | null
  created_at: string
  title: string | null
}

export interface RequiresActionDetail {
  tool_name: string
  display_tool_name?: string
  action_description: string
  raw_command?: string
  request_id: string
  tool_use_id: string
}

export interface EventOut {
  /** v2: Redis Stream ID '<ms>-<seq>'; lex-comparable, monotonic. */
  stream_id: string
  session_id: string
  type: string
  payload: Record<string, unknown>
  origin: 'worker' | 'mobile' | 'system'
}

export interface PairHandoffResult {
  token: string
  expires_in: number
  user_id: string
  worker_id: string
  dispatch_space_id: string
  space_id: string
  space_title: string | null
}

// ── Auth / pair ──────────────────────────────────────────────────

/** Scan QR → fresh mobile JWT (24h). No prior JWT required. */
export async function handoffPair(code: string): Promise<PairHandoffResult> {
  // Use raw fetch (skip authHeaders) — handoff is exempt from JWT
  const res = await fetch(`${DECK_BASE}/dispatch/pair/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  if (!res.ok) {
    let body = ''
    try { body = await res.text() } catch {}
    throw new ApiError(`HTTP ${res.status}`, res.status, body)
  }
  return (await res.json()) as PairHandoffResult
}

// ── Spaces ───────────────────────────────────────────────────────

export async function listSpaces(): Promise<{ spaces: SpaceOut[] }> {
  return await apiFetch('/dispatch/spaces')
}

export async function listSpaceSessions(dispatchSpaceId: string): Promise<{ sessions: SessionOut[] }> {
  return await apiFetch(`/dispatch/spaces/${dispatchSpaceId}/sessions`)
}

export async function createSpaceTask(
  dispatchSpaceId: string,
  prompt: string,
  attachments: AttachmentRef[] = [],
): Promise<{ session_id: string }> {
  // v2.1: 手机生成 session id (UUID) — 全链路单 id，桌面直接拿它当 cowork sessionId
  const session_id = crypto.randomUUID()
  return await apiFetch(`/dispatch/spaces/${dispatchSpaceId}/tasks`, {
    method: 'POST',
    body: JSON.stringify({ session_id, prompt, attachments }),
  })
}

/** 语音新建任务（b 方案）— 录音直传 R2，upload_id 发桌面，桌面转写后直接派。 */
export async function sendVoiceTask(
  dispatchSpaceId: string,
  audio: Blob,
  onProgress?: (frac: number) => void,
): Promise<{ session_id: string }> {
  const session_id = crypto.randomUUID()
  const upload_id = await uploadAudio(audio, onProgress)
  return await apiFetch(`/dispatch/spaces/${dispatchSpaceId}/voice-tasks`, {
    method: 'POST',
    body: JSON.stringify({ session_id, upload_id }),
  })
}

// ── Per-session events / actions ─────────────────────────────────

export async function getSessionEvents(
  sessionId: string,
  since = '0',
  limit = 200,
): Promise<{ events: EventOut[]; next_since: string | null; replay_pending?: boolean }> {
  return await apiFetch(`/dispatch/sessions/${sessionId}/events?since=${encodeURIComponent(since)}&limit=${limit}`)
}

export async function sendMessage(
  sessionId: string,
  content: string,
  attachments: AttachmentRef[] = [],
): Promise<{ message_id: string; queued_at: string }> {
  return await apiFetch(`/dispatch/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content, attachments }),
  })
}

/** 语音续消息（b 方案）— 录音直传 R2，upload_id 发桌面，桌面转写后续跑。 */
export async function sendVoiceMessage(
  sessionId: string,
  audio: Blob,
  onProgress?: (frac: number) => void,
): Promise<{ message_id: string }> {
  const upload_id = await uploadAudio(audio, onProgress)
  return await apiFetch(`/dispatch/sessions/${sessionId}/voice-messages`, {
    method: 'POST',
    body: JSON.stringify({ upload_id }),
  })
}

export async function postPermissionDecision(
  sessionId: string,
  requestId: string,
  decision: 'allow' | 'deny' | 'allow_always',
  scope: 'once' | 'session' | 'always' = 'once',
): Promise<void> {
  await apiFetch(`/dispatch/sessions/${sessionId}/permission`, {
    method: 'POST',
    body: JSON.stringify({ request_id: requestId, decision, scope }),
  })
}

export async function postSessionControl(sessionId: string, action: 'pause' | 'resume' | 'cancel'): Promise<void> {
  await apiFetch(`/dispatch/sessions/${sessionId}/control`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  })
}
